import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Wallet, QrCode, ExternalLink, ArrowRight, GraduationCap, Loader2,
  CheckCircle2, LogIn, Download, Copy, ChevronDown, ChevronUp,
} from "lucide-react";
import { toast } from "sonner";

/**
 * PUBLIC payment-instructions page — no login required.
 *
 * Landing page linked from the mobile app (via <ContinueOnWebCard/>) so
 * students who prefer to pay via GCash / Maya / bank transfer instead of
 * Apple IAP or Google Play Billing can see the step-by-step here without
 * needing to sign in first. Once they've paid, they sign in on their
 * mobile app or the website with the same email and access unlocks after
 * admin verification.
 *
 * IMPORTANT: This page must NEVER be linked from the native iOS/Android app
 * with wording that says "cheaper on the web" — Apple 3.1.3(b) allows an
 * account-management link but forbids any anti-steering language. Copy on
 * this page also avoids explicit price comparisons for that reason.
 */
export default function PaymentInstructionsPage() {
  const [paymongo, setPaymongo] = useState(null);
  const [systems, setSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null); // system id currently expanded

  useEffect(() => {
    Promise.all([
      api.get("/settings/payment/public").catch(() => ({ data: null })),
      api.get("/payment-systems/public").catch(() => ({ data: [] })),
    ]).then(([p, s]) => {
      setPaymongo(p.data || null);
      setSystems(s.data || []);
    }).finally(() => setLoading(false));
  }, []);

  const paymongoActive = !!(paymongo?.paymongo_enabled && paymongo?.paymongo_payment_link);

  const copy = async (text, label = "Copied") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch { toast.error("Copy failed — please long-press to copy manually"); }
  };

  const downloadQr = (ps) => {
    const url = ps.qr_code_data_url;
    if (!url) return;
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(ps.name || "payment").replace(/\s+/g, "-").toLowerCase()}-qr.png`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-indigo-50 via-white to-white">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-900 hover:text-indigo-700 transition">
            <GraduationCap className="w-6 h-6 text-indigo-600" />
            <span className="font-display font-bold text-lg">PrepMasters</span>
          </Link>
          <Link to="/login">
            <Button variant="ghost" size="sm" className="text-slate-700" data-testid="pi-signin-nav-btn">
              <LogIn className="w-4 h-4 mr-1.5" /> Sign In
            </Button>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900" data-testid="pi-title">
          How to Pay for Your Subscription
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Follow the steps below to pay via GCash, Maya, or bank transfer. After payment, sign in with
          the email you used to register and your access will be activated after admin verification
          (usually within 24 hours).
        </p>

        {/* Timeline of the 4 steps */}
        <Card className="mt-6 p-5 sm:p-6 border-indigo-100 bg-indigo-50/40">
          <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-indigo-700 mb-3">
            Overview — 4 simple steps
          </div>
          <ol className="space-y-3 text-sm text-slate-800">
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold grid place-items-center shrink-0">1</span> Pick your preferred payment method below.</li>
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold grid place-items-center shrink-0">2</span> Scan the QR or send payment to the account shown, note your reference number.</li>
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold grid place-items-center shrink-0">3</span> Sign in to your PrepMasters account and upload your receipt + reference number.</li>
            <li className="flex gap-3"><span className="h-6 w-6 rounded-full bg-indigo-600 text-white text-xs font-bold grid place-items-center shrink-0">4</span> An administrator verifies your payment (usually within 24 hours) and your access unlocks.</li>
          </ol>
        </Card>

        <h2 className="mt-8 text-lg font-display font-bold text-slate-900 flex items-center gap-2">
          <Wallet className="w-5 h-5 text-indigo-600" /> Available payment methods
        </h2>

        {loading ? (
          <div className="mt-6 flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {paymongoActive && (
              <Card className="p-4 border-emerald-200 bg-emerald-50/40">
                <div className="flex items-center gap-3">
                  <div className="h-11 w-11 rounded-md bg-emerald-600 grid place-items-center">
                    <Wallet className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-emerald-900">PayMongo (Card, GCash, Maya, Online Banking)</div>
                    <div className="text-xs text-slate-600">Instant checkout — completes payment in one page.</div>
                  </div>
                </div>
                <a href={paymongo.paymongo_payment_link} target="_blank" rel="noreferrer" data-testid="pi-open-paymongo">
                  <Button className="w-full mt-3 bg-emerald-600 hover:bg-emerald-700 text-white">
                    Open Checkout <ExternalLink className="w-3.5 h-3.5 ml-2" />
                  </Button>
                </a>
                {paymongo?.payment_instructions && (
                  <p className="text-[12px] text-emerald-900 mt-3 whitespace-pre-line">{paymongo.payment_instructions}</p>
                )}
              </Card>
            )}

            {systems.length === 0 && !paymongoActive && (
              <Card className="p-6 border-amber-200 bg-amber-50 text-sm text-amber-900">
                We're currently updating our payment methods. Please check back shortly or contact
                <a href="mailto:support@prepmaster.ph" className="font-semibold underline mx-1">support@prepmaster.ph</a>
                for assistance.
              </Card>
            )}

            {systems.map((ps) => {
              const open = expanded === ps.id;
              return (
                <Card key={ps.id} className="border-slate-200 overflow-hidden" data-testid={`pi-system-${ps.id}`}>
                  <button
                    type="button"
                    className="w-full text-left p-4 flex items-center gap-3 hover:bg-slate-50 transition"
                    onClick={() => setExpanded(open ? null : ps.id)}
                    data-testid={`pi-system-toggle-${ps.id}`}
                  >
                    <div className="w-11 h-11 rounded-md border border-slate-200 grid place-items-center bg-white overflow-hidden shrink-0">
                      {ps.logo_data_url ? (
                        <img src={ps.logo_data_url} alt={`${ps.name} logo`} className="w-full h-full object-contain" />
                      ) : ps.qr_code_data_url ? (
                        <img src={ps.qr_code_data_url} alt="" className="w-full h-full object-contain" />
                      ) : (
                        <QrCode className="w-5 h-5 text-slate-400" strokeWidth={1.5} />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-slate-900">{ps.name}</div>
                      {/* Account name/number intentionally hidden in the header
                          row — students see the full account details (with
                          copy buttons) once they tap to expand this method. */}
                    </div>
                    {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                  </button>

                  {open && (
                    <div className="p-4 border-t border-slate-100 bg-slate-50/60 space-y-4">
                      {ps.qr_code_data_url && (
                        <div className="flex flex-col items-center bg-white border border-slate-200 rounded-md p-4">
                          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-2 flex items-center gap-1">
                            <QrCode className="w-3 h-3" /> Scan to pay
                          </div>
                          <img
                            src={ps.qr_code_data_url}
                            alt={`${ps.name} QR code`}
                            className="w-48 h-48 sm:w-56 sm:h-56 object-contain"
                            data-testid={`pi-qr-${ps.id}`}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-3"
                            onClick={() => downloadQr(ps)}
                            data-testid={`pi-qr-download-${ps.id}`}
                          >
                            <Download className="w-3.5 h-3.5 mr-1.5" /> Download QR
                          </Button>
                        </div>
                      )}

                      {(ps.account_name || ps.account_number) && (
                        <div className="text-sm space-y-2">
                          {ps.account_name && (
                            <div className="flex items-center justify-between gap-2 bg-white p-2.5 rounded border border-slate-200">
                              <div><span className="text-slate-500 text-xs mr-2">Account Name</span>{ps.account_name}</div>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => copy(ps.account_name, "Account name copied")} data-testid={`pi-copy-name-${ps.id}`}>
                                <Copy className="w-3 h-3 mr-1" /> Copy
                              </Button>
                            </div>
                          )}
                          {ps.account_number && (
                            <div className="flex items-center justify-between gap-2 bg-white p-2.5 rounded border border-slate-200">
                              <div><span className="text-slate-500 text-xs mr-2">Account Number</span><span className="font-mono">{ps.account_number}</span></div>
                              <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => copy(ps.account_number, "Account number copied")} data-testid={`pi-copy-number-${ps.id}`}>
                                <Copy className="w-3 h-3 mr-1" /> Copy
                              </Button>
                            </div>
                          )}
                        </div>
                      )}

                      {ps.link && (
                        <a href={ps.link} target="_blank" rel="noreferrer" data-testid={`pi-open-link-${ps.id}`}>
                          <Button variant="outline" className="w-full">
                            <ExternalLink className="w-3.5 h-3.5 mr-2" /> Open {ps.name} app / page
                          </Button>
                        </a>
                      )}

                      {ps.instructions && (
                        <div className="text-[13px] text-slate-700 whitespace-pre-line bg-white p-3 rounded border border-slate-200">
                          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500 mb-1.5">Instructions</div>
                          {ps.instructions}
                        </div>
                      )}
                    </div>
                  )}
                </Card>
              );
            })}
          </div>
        )}

        {/* Next steps card */}
        <Card className="mt-8 p-5 sm:p-6 border-indigo-200 bg-gradient-to-br from-indigo-50 to-white">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="w-6 h-6 text-indigo-600 shrink-0" />
            <div className="flex-1">
              <h3 className="font-display text-lg font-bold text-slate-900">After you've paid</h3>
              <p className="text-sm text-slate-700 mt-1">
                Sign in with the email you used to register, go to <span className="font-semibold">Enrollments</span>,
                and upload your receipt with the reference number.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Link to="/login" data-testid="pi-signin-cta">
                  <Button className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <LogIn className="w-4 h-4 mr-2" /> Sign In to Upload Receipt
                  </Button>
                </Link>
                <Link to="/register" data-testid="pi-register-cta">
                  <Button variant="outline">
                    Create Free Account <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </Card>

        <p className="mt-6 text-[11px] text-slate-500 text-center">
          Need help? Email <a href="mailto:support@prepmaster.ph" className="underline">support@prepmaster.ph</a>
        </p>
      </main>
    </div>
  );
}
